import React, {useState, useMemo, memo, useCallback, useEffect} from 'react';
import {
  Box,
  Table,
  TableBody,
  TableFooter,
  TableHead,
  Typography,
  Skeleton,
  Stack,
  InputAdornment,
  Tooltip,
  Link,
} from '@mui/material';
import {debounce, has} from 'lodash';
import {WorkbookPageHeader} from '../WorkbookPageHeader';
import {APP_HEADER_HEIGHT} from '../../../../App';
import {
  useGetRuleMetadataQuery,
  useGetRuleTransactionsQuery,
  useReclassifyIndirectFSLIMutation,
  useGetIndirectCashflowWorkbookQuery,
} from '../../../../data/api';
import {useLocation, useNavigate} from 'react-router-dom';
import {renderAmount, triggerDownload} from '../../../../util';
import {ReclassifyModal} from './ReclassifyModal';
import {ActionButtonMenu} from './ActionButtonMenu';
import {LoadingTransactionTable} from './LoadingTransactionTable';
import {ReclassifySnackbar} from './ReclassifySnackbar';
import {SearchIcon, DownloadIcon} from 'lucide-react';
import {useParamReportPeriodOrDefault} from '../../../../util/useParamReportPeriodOrDefault';
import {
  StyledBodyCell,
  StyledHeadCell,
  StyledTableRow,
  StyledFooterCell,
  StyledTableContainer,
  StyledOutlinedInput,
} from '../components';
import {filterByQuery} from '../filterByQuery';
import {SignedAccountsCell} from './SignedAccountsCell';
import LoadingButton from '@mui/lab/LoadingButton';
import {useSelector} from 'react-redux';
import * as api from '../../../../util/api';

function TransactionsPage() {
  const {state = {}} = useLocation();
  const navigate = useNavigate();
  const {pliId, indirectPliId} = state;
  const period = useParamReportPeriodOrDefault();
  const [activeTransaction, setActiveTransaction] = useState(null);
  const [snackbarData, setSnackbarData] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const {currentOrg} = useSelector((state) => state.session);

  const {data: cashflowWorkbookData} = useGetIndirectCashflowWorkbookQuery(
    {period},
    {skip: !period}
  );
  const validIndirectPliIds = (cashflowWorkbookData || [])
    .filter((d) => d.pli_id === pliId)
    .map((d) => d.indirect_pli_id);

  useEffect(() => {
    if (!pliId || !indirectPliId) {
      navigate('/statements/indirect-cashflow-workbook');
    }
  }, [pliId, indirectPliId]);

  const [reclassifyIndirectFSLI] = useReclassifyIndirectFSLIMutation();
  const {data: ruleMetadata, isFetching: isFetchingMetadata} =
    useGetRuleMetadataQuery({pliId, indirectPliId, period});
  const {data: ruleTransactions, isFetching: isFetchingTransactions} =
    useGetRuleTransactionsQuery({pliId, indirectPliId, period});

  async function onReclassify(newIndirectPli, name) {
    const signedAccountId = activeTransaction.signed_account_id;
    setActiveTransaction(null);
    try {
      const {error} = await reclassifyIndirectFSLI({
        signedAccountId,
        pliId: newIndirectPli,
      });
      if (error) {
        throw new Error(error);
      }

      setSnackbarData({
        message: `This changed moved all associated transactions to ${name}.`,
        severity: 'success',
      });
    } catch (err) {
      setSnackbarData({
        message: 'An error occurred',
        severity: 'error',
      });
    }
    setActiveTransaction(null);
  }

  // Manage the query string and debouncing for performance
  const [query, setQuery] = useState('');
  const debouncedSetQuery = useMemo(() => debounce(setQuery, 300), [setQuery]);
  const handleChange = useCallback(
    (e) => {
      debouncedSetQuery(e.target.value);
    },
    [debouncedSetQuery]
  );

  const filteredRuleTransactions = useMemo(() => {
    return filterByQuery(ruleTransactions, query, {
      numericField: 'amount',
      textFields: ['signed_account', 'signed_accounts', 'document_number'],
    });
  }, [ruleTransactions, query]);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const _filename = `${has(currentOrg, 'org_metadata.companyLegalName') ? currentOrg.org_metadata.companyLegalName : currentOrg.orgName} - ${ruleMetadata?.name} - Transactions - ${period}.xlsx`;
      await triggerDownload(
        () => api.downloadRuleTransactions({pliId, indirectPliId, period}),
        _filename
      );
    } catch (_error) {
      console.error(_error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <WorkbookPageHeader
        title={
          isFetchingMetadata ? (
            <Skeleton width="400px" sx={{display: 'inline-block'}} />
          ) : (
            ruleMetadata?.name
          )
        }
      >
        <Box
          display="flex"
          width="100%"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box />
          <LoadingButton
            onClick={handleDownload}
            loading={isDownloading}
            disabled={!period || isFetchingMetadata || isFetchingTransactions}
            startIcon={<DownloadIcon size={16} />}
            variant="contained"
            color="green"
          >
            Download Transactions
          </LoadingButton>
        </Box>
      </WorkbookPageHeader>
      <Box
        mt={APP_HEADER_HEIGHT}
        sx={{
          flex: 1,
          overflow: 'hidden',
        }}
      >
        <Stack
          width="100%"
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          py={2}
          px={3}
        >
          <StyledOutlinedInput
            onInput={handleChange}
            startAdornment={
              <InputAdornment position="start">
                <SearchIcon size={16} />
              </InputAdornment>
            }
            placeholder="Internal ID, account line, amount"
          />
          <Tooltip
            title={ruleMetadata?.classification_desc}
            enterDelay={1000}
            placement="bottom-end"
          >
            <Typography
              fontSize="12px"
              mx={3}
              my={2}
              width="100%"
              whiteSpace="nowrap"
              textAlign="right"
              textOverflow="ellipsis"
              overflow="hidden"
            >
              <Typography fontSize="12px" fontWeight="bold" component="span">
                Classification rule =
              </Typography>{' '}
              {isFetchingMetadata ? (
                <Skeleton width="400px" sx={{display: 'inline-block'}} />
              ) : (
                ruleMetadata?.classification_desc
              )}
            </Typography>
          </Tooltip>
        </Stack>
        <StyledTableContainer>
          {isFetchingTransactions ? (
            <LoadingTransactionTable />
          ) : (
            <Table
              stickyHeader
              sx={{borderCollapse: 'separate', 'p, span': {fontSize: 12}}}
            >
              <TableHead>
                <StyledTableRow sx={{border: '0 !important'}}>
                  <StyledHeadCell>
                    <Typography>Internal ID</Typography>
                  </StyledHeadCell>
                  <StyledHeadCell>
                    <Typography>Classified JE Account Line</Typography>
                  </StyledHeadCell>
                  <StyledHeadCell>
                    <Typography>JE Account lines</Typography>
                  </StyledHeadCell>
                  <StyledHeadCell>
                    <Typography>Amount</Typography>
                  </StyledHeadCell>
                  <StyledHeadCell
                    sx={{width: '50px', minWidth: '50px', maxWidth: '50px'}}
                  />
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {!isFetchingTransactions &&
                  filteredRuleTransactions.length === 0 && (
                    <StyledTableRow>
                      <StyledBodyCell
                        colSpan={5}
                        sx={{
                          borderBottom: 0,
                          textAlign: 'center',
                        }}
                      >
                        <Typography sx={{fontWeight: 500}}>
                          No results found
                        </Typography>
                      </StyledBodyCell>
                    </StyledTableRow>
                  )}

                {filteredRuleTransactions.map((transaction, idx) => (
                  <StyledTableRow
                    key={`${transaction.document_number}-${idx}`}
                    sx={{
                      ':last-child > td': {
                        borderBottom: 0,
                      },
                    }}
                  >
                    <StyledBodyCell>
                      <Link
                        href={`https://system.netsuite.com/app/accounting/transactions/transaction.nl?id=${transaction.document_number}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{fontSize: '12px'}}
                      >
                        {transaction.document_number}
                      </Link>
                    </StyledBodyCell>
                    <StyledBodyCell>
                      <Typography>{transaction.signed_account}</Typography>
                    </StyledBodyCell>
                    <SignedAccountsCell
                      signedAccounts={transaction.signed_accounts}
                    />
                    <StyledBodyCell>
                      <Typography align="right">
                        {renderAmount(transaction.amount)}
                      </Typography>
                    </StyledBodyCell>
                    <StyledBodyCell sx={{width: '50px', textAlign: 'center'}}>
                      <ActionButtonMenu
                        popupId={`${transaction.document_number}-${idx}`}
                        onClick={() => setActiveTransaction(transaction)}
                      />
                    </StyledBodyCell>
                  </StyledTableRow>
                ))}
              </TableBody>
              <TableFooter sx={{position: 'sticky', bottom: 0}}>
                <StyledTableRow>
                  <StyledFooterCell colSpan={3} />
                  <StyledFooterCell sx={{width: '300px'}}>
                    <Stack spacing={0.5}>
                      <Box display="flex" gap={2}>
                        <Typography fontSize="12px" sx={{flex: 1}}>
                          Transactions total:
                        </Typography>
                        <Typography
                          fontSize="12px"
                          sx={{width: '100px', textAlign: 'right'}}
                        >
                          {renderAmount(ruleMetadata?.original_amount)}
                        </Typography>
                      </Box>
                      <Box display="flex" gap={2}>
                        <Typography fontSize="12px" sx={{flex: 1}}>
                          Less transactions already accounted for:
                        </Typography>
                        <Typography
                          fontSize="12px"
                          sx={{width: '100px', textAlign: 'right'}}
                        >
                          {renderAmount(
                            ruleMetadata?.default_duplicate_transaction_amount
                          )}
                        </Typography>
                      </Box>
                      <Box display="flex" gap={2}>
                        <Typography fontSize="12px" sx={{flex: 1}}>
                          Plus Fx posting rate contribution:
                        </Typography>
                        <Typography
                          fontSize="12px"
                          sx={{width: '100px', textAlign: 'right'}}
                        >
                          {renderAmount(
                            ruleMetadata?.default_net_posting_rate_difference_amount
                          )}
                        </Typography>
                      </Box>
                      <Box display="flex" gap={2}>
                        <Typography
                          fontSize="12px"
                          fontWeight="bold"
                          sx={{flex: 1}}
                        >
                          Total SCF Impact:
                        </Typography>
                        <Typography
                          fontSize="12px"
                          fontWeight="bold"
                          sx={{
                            width: '100px',
                            textAlign: 'right',
                            borderTop: '1px solid #9BA5A0',
                            borderBottom: '3px double #9BA5A0',
                            marginTop: '-4px',
                            marginBottom: '-4px',
                            paddingTop: '4px',
                            paddingBottom: '4px',
                          }}
                        >
                          {renderAmount(ruleMetadata?.amount)}
                        </Typography>
                      </Box>
                    </Stack>
                  </StyledFooterCell>
                  <StyledFooterCell />
                </StyledTableRow>
              </TableFooter>
            </Table>
          )}
        </StyledTableContainer>
      </Box>

      {ruleMetadata && ruleTransactions && (
        <>
          <ReclassifyModal
            open={Boolean(activeTransaction)}
            validIndirectPliIds={validIndirectPliIds}
            indirectPliId={ruleMetadata.indirect_pli_id}
            documentNumber={activeTransaction?.document_number}
            signedAccount={activeTransaction?.signed_account}
            amount={activeTransaction?.amount}
            onClose={() => setActiveTransaction(null)}
            onReclassify={onReclassify}
          />
          <ReclassifySnackbar
            open={Boolean(snackbarData)}
            onClose={() => setSnackbarData(null)}
            message={snackbarData?.message}
            severity={snackbarData?.severity}
          />
        </>
      )}
    </>
  );
}

export default memo(TransactionsPage);
